import { useRoute } from 'nuxt/app'
import type { Pinia } from 'pinia'
import { resolveLocalAuthCookieUser, resolveRestoredAuthUser } from '../utils/auth-cookie'
import { resolveDefaultLoginRedirectUrl, resolveLoginRedirectUrl } from '../utils/login-redirect'

const YEAR_SECONDS = 60 * 60 * 24 * 365
const AUTH_COOKIE_NAMES = ['DJkdikKMG', 'ViLartidlakd', 'WsdsfgDGdagYms'] as const
export const LOGIN_PHONE_COUNTRY_COOKIE_NAME = 'wikibit-login-phone-country'

export type LoginPhoneCountry = {
  areaCode: string
  areaFlag: string
}

const extractCookieValue = (response: any) =>
  response?.result?.Value ||
  response?.data?.Value ||
  response?.usercenter?.getcookie?.result?.Value ||
  response?.usercenter?.getcookie?.data?.Value ||
  response?.usercenter?.getcookie?.Value ||
  ''

const extractUserInfo = (response: any) =>
  response?.result ||
  response?.data ||
  response?.usercenter?.getuserfromcookie?.result ||
  response?.usercenter?.getuserfromcookie?.data ||
  null

const postToParent = (payload: Record<string, any>) => {
  if (!import.meta.client) {
    return
  }

  window.parent?.postMessage(payload, '*')
}

const getCookieSecurityOptions = () => {
  if (!import.meta.client || window.location.protocol !== 'https:') {
    return {}
  }

  return {
    sameSite: 'none' as const,
    secure: true,
  }
}

/** 只保留登录响应中的手机号国家信息，避免持久化完整用户响应或登录令牌。 */
const resolveLoginPhoneCountry = (userInfo: Record<string, any> | null | undefined): LoginPhoneCountry | null => {
  const areaCode = String(userInfo?.areaCode ?? userInfo?.AreaCode ?? '').trim()

  if (!areaCode) {
    return null
  }

  return {
    areaCode,
    areaFlag: String(userInfo?.areaFlag ?? userInfo?.AreaFlag ?? '').trim(),
  }
}

const setLoginTokenCookies = (userInfo: any) => {
  const loginToken = userInfo?.LoginToken || userInfo

  if (!loginToken?.expires_in || !loginToken?.access_token) {
    return
  }

  const maxAge = Number(loginToken.expires_in)
  useAppCookie('logintoken', { maxAge }).value = loginToken.access_token
  useAppCookie('access_token', { maxAge }).value = loginToken.access_token
  useAppCookie('refresh_token', { maxAge }).value = loginToken.refresh_token || ''
  useAppCookie('refreshtime', { maxAge }).value = String(Date.now())
}

export const useLogin = (pinia?: Pinia) => {
  const appStore = useAppStore(pinia)
  const route = useRoute()
  const { getUserCookie, getUserFromCookie, invokeV3 } = useLoginApi()
  const loginPhoneCountryCookie = useAppObjectCookie<LoginPhoneCountry>(LOGIN_PHONE_COUNTRY_COOKIE_NAME, null, {
    maxAge: YEAR_SECONDS,
    ...getCookieSecurityOptions(),
  })

  /** 同步登录响应中的手机号国家信息到 Store 和持久化 Cookie。 */
  const persistLoginPhoneCountry = (userInfo: Record<string, any> | null | undefined) => {
    const phoneCountry = resolveLoginPhoneCountry(userInfo)

    if (!phoneCountry) {
      loginPhoneCountryCookie.value = null
      appStore.setUser({ areaCode: '', areaFlag: '' })
      return null
    }

    loginPhoneCountryCookie.value = phoneCountry
    appStore.setUser(phoneCountry)
    return phoneCountry
  }

  const resolveAuthCookieName = () => {
    if (appStore.isWiki === '1') return 'DJkdikKMG'
    if (appStore.isWiki === '2') return 'ViLartidlakd'
    return 'WsdsfgDGdagYms'
  }

  const readAuthCookieValue = () => {
    const preferredCookieName = resolveAuthCookieName()
    const cookieNames = [preferredCookieName, ...AUTH_COOKIE_NAMES.filter((name) => name !== preferredCookieName)]

    for (const cookieName of cookieNames) {
      const cookie = useAppCookie(cookieName)
      if (typeof cookie.value === 'string' && cookie.value) {
        return cookie.value
      }
    }

    return ''
  }

  const fetchAuthCookieValue = async (userInfo: Record<string, any>) => {
    const directResponse = await getUserCookie(userInfo).catch(() => null)
    const directCookie = extractCookieValue(directResponse)
    if (directCookie) {
      return directCookie
    }

    const invokeResponse = await invokeV3('usercenter$getcookie', userInfo).catch(() => null)
    return extractCookieValue(invokeResponse)
  }

  const fetchUserInfoFromCookie = async (cookieValue: string) => {
    if (!cookieValue) {
      return null
    }

    const response = await getUserFromCookie({ contentcookie: cookieValue }).catch(() => null)
    return extractUserInfo(response)
  }

  const writeAuthCookie = (cookieValue: string) => {
    const cookieName = resolveAuthCookieName()
    const securityOptions = getCookieSecurityOptions()

    clearAppCookies(AUTH_COOKIE_NAMES.filter((name) => name !== cookieName))

    useAppCookie(cookieName, {
      maxAge: YEAR_SECONDS,
      ...securityOptions,
    }).value = cookieValue
    appStore.setUsercookie(cookieValue)
  }

  const clearLoginCookies = () => {
    clearAppCookies([
      ...AUTH_COOKIE_NAMES,
      'userid',
      'logintoken',
      'access_token',
      'refresh_token',
      'refreshtime',
      'userbaseinfo',
      'nickavatar',
      LOGIN_PHONE_COUNTRY_COOKIE_NAME,
    ])
    if (import.meta.client) {
      window.localStorage.removeItem('shared-app-user')
    }
    appStore.setUsercookie('')
    appStore.setUser({})
  }

  const afterLoginSuccess = (cookieValue: string) => {
    appStore.setLoginShow(false)
    postToParent({ op_type: 'reload', url: '', parm: cookieValue })

    if (import.meta.client && String(route.name || '').startsWith('userlogin') && !appStore.userlogin2) {
      const redirectUrl = resolveLoginRedirectUrl(
        route.query.returnurl || route.query.from || route.query.fromurl,
        resolveDefaultLoginRedirectUrl(appStore.LanguageCode),
        window.location.origin,
      )
      window.setTimeout(() => {
        window.location.href = redirectUrl
      }, 1200)
    }
  }

  const setCookieFromThirdParty = async (cookieValue: string, userInfo: Record<string, any> = {}) => {
    if (userInfo.userId || userInfo.nick || userInfo.avatar) {
      appStore.setUser(userInfo)
    }
    persistLoginPhoneCountry(userInfo)

    writeAuthCookie(cookieValue)
    const userFromCookie = await fetchUserInfoFromCookie(cookieValue)
    if (userFromCookie) {
      appStore.setUser({
        userId: userFromCookie.userId || '',
        nick: userFromCookie.nick || userFromCookie.nickName || '',
        avatar: userFromCookie.avatar || userFromCookie.profilePhoto || '',
      })
    }
    afterLoginSuccess(cookieValue)
  }

  const restoreLoginState = async () => {
    if (appStore.contentcookie && appStore.user?.userId) return

    const cookieValue = appStore.contentcookie || readAuthCookieValue()

    if (!cookieValue) return

    const storedUserId = useAppCookie('userid').value || ''
    const storedPhoneCountry = loginPhoneCountryCookie.value || {}
    const localCookieUser = resolveLocalAuthCookieUser(cookieValue, storedUserId)
    if (localCookieUser) {
      appStore.setUsercookie(cookieValue)
      appStore.setUser({ ...localCookieUser, ...storedPhoneCountry })
      return
    }

    const userInfo = await fetchUserInfoFromCookie(cookieValue)
    const cookieUser = resolveRestoredAuthUser(cookieValue, storedUserId, {
      userId: userInfo?.userId || '',
      nick: userInfo?.nick || userInfo?.nickName || '',
      avatar: userInfo?.avatar || userInfo?.profilePhoto || '',
    })
    appStore.setUsercookie(cookieValue)
    if (cookieUser) {
      appStore.setUser({ ...cookieUser, ...storedPhoneCountry })
    }
  }

  const accessCookie = async (userInfo: any) => {
    const phoneCountry = resolveLoginPhoneCountry(userInfo)
    const mergedUser = {
      userId: userInfo?.userId || '',
      nick: userInfo?.showNickName || userInfo?.nick || userInfo?.nickName || '',
      avatar: userInfo?.showAvataraddress || userInfo?.avatar || userInfo?.profilePhoto || '',
      areaCode: phoneCountry?.areaCode || '',
      areaFlag: phoneCountry?.areaFlag || '',
    }

    appStore.setUser(mergedUser)
    persistLoginPhoneCountry(userInfo)
    appStore.setLoginverify(false)
    setLoginTokenCookies(userInfo)

    const cookieValue = await fetchAuthCookieValue(mergedUser)

    if (cookieValue) {
      writeAuthCookie(cookieValue)
      afterLoginSuccess(cookieValue)
    }

    return cookieValue
  }

  return {
    accessCookie,
    clearLoginCookies,
    restoreLoginState,
    setLoginTokenCookies,
    setCookieFromThirdParty,
    writeAuthCookie,
  }
}
